/**
 * audioTypes — the engine-neutral data model for the audio system (P0, L1–L4).
 *
 * Pure types + descriptors, ZERO Web Audio / DOM. This is the "what a sound IS"
 * half of the engine; the AudioEngine (later) owns the AudioContext + node graph
 * that turns these descriptors into sound. Keeping this DOM-free makes the model
 * unit-testable in node AND liftable to the Rust ports (helix-audio) verbatim.
 *
 * Architecture: docs/architecture/AUDIO_SYSTEM_ARCHITECTURE.md (§4–§5 cue model).
 */

/** The five mix buses. `spatial` routes through `sfx` for volume but adds a panner stage. */
export type BusName = 'sfx' | 'ui' | 'ambient' | 'music' | 'spatial';

export const BUS_NAMES: readonly BusName[] = ['sfx', 'ui', 'ambient', 'music', 'spatial'];

/** A single synth grain. `sweepTo` (Hz) pitches the tone toward a target over its life;
 *  omitted = the legacy default of `freq * 0.3` (floored at 20Hz), preserving current feel. */
export interface ToneDesc {
  kind: 'tone';
  freq: number;          // Hz
  osc: OscillatorType;   // 'sine' | 'square' | 'sawtooth' | 'triangle'
  durationMs: number;
  gain?: number;         // 0..1 relative to the cue (default 1)
  sweepTo?: number;      // Hz target; default freq * 0.3
}

/** Filtered noise — wind/surf/impact texture. */
export interface NoiseDesc {
  kind: 'noise';
  durationMs: number;
  gain?: number;
  filter?: { type: BiquadFilterType; freq: number };
}

/** A sampled one-shot (W4.2 / P7). `track` is the pre-loaded sample track index. */
export interface SampleDesc {
  kind: 'sample';
  /** Pre-loaded sample track index in the wasm engine. */
  track: number;
  gain?: number;
}

/** An ordered set of grains with per-step delays — arpeggios, two-layer hits, fanfares. */
export interface SequenceDesc {
  kind: 'sequence';
  steps: SequenceStep[];
}
export interface SequenceStep {
  desc: ToneDesc | NoiseDesc;
  delayMs: number;       // offset from the cue's start
}

export type SoundDesc = ToneDesc | NoiseDesc | SampleDesc | SequenceDesc;

/** A Cue is the authored unit of sound. The SoundBank maps EventName → cueId(s). */
export interface Cue {
  id: string;                      // 'combat.melee.hit' — domain.subject.action
  bus: BusName;
  priority: number;                // 0..1 — drives ducking + voice-stealing
  sound: SoundDesc | SoundDesc[];  // array = a variation set (round-robin / random)
  pitchJitter?: number;            // ± semitones applied per play (anti-repetition)
  minIntervalMs?: number;          // dedup window — collapse same cue fired within this
  maxStack?: number;               // max concurrent voices of this cue (default unbounded)
  spatial?: boolean;               // requires an emitter position (L5)
}

/** An event→cue binding row (codegen-emitted from cue.toml). */
export interface EventBinding {
  event: string;
  cues: string[];
}

/** Resolved, flat synth params handed to the (future) Web Audio plumbing. Pure data —
 *  the exponential gain decay + freq sweep that the legacy `playSFX` baked in are encoded
 *  here so behavior is identical and the mapping is unit-testable. */
export interface ToneParams {
  freq: number;
  sweepTo: number;
  osc: OscillatorType;
  durationSec: number;
  peakGain: number;     // gain at attack (already includes the 0.15 legacy headroom)
  endGain: number;      // exp-decay target (0.001 legacy)
}
